import React from "react";
import { ArrowRight, CheckCircle2, Star } from "lucide-react";
import { ButtonLink } from "../../../../components/ui/button";
import { FadeUp } from "./FadeUp";
import type { RouteMap } from "../../types";

const PRICING_FEATURES = [
  "A live site neighbors can find",
  "Quote requests while you are on a job",
  "Local search pages for your services and towns",
  "Job photos from the website or the Toolkit app",
  "License tracking and expiry reminders",
  "GPS matching and tags for photos",
  "Hosted on your toolkit.build address",
];

type PricingSectionProps = {
  routes: RouteMap;
};

export const PricingSection: React.FC<PricingSectionProps> = ({ routes }) => (
  <section id="pricing" className="py-24 bg-background border-t border-border">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeUp>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block py-1.5 px-4 rounded-full bg-premium-50 text-premium-600 font-bold text-sm mb-4 uppercase tracking-wider">
            30 days free
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6 tracking-tight font-display">
            $9/month
          </h2>
          <div className="w-24 h-1.5 bg-premium-500 mx-auto mb-8 rounded-full" />
          <p className="text-xl text-muted-foreground font-medium">
            Then one bill for the website, job photos, and licenses. Built for
            sole trades and small crews.
          </p>
        </div>
      </FadeUp>

      <FadeUp delay={0.2}>
        <div className="max-w-lg mx-auto bg-card rounded-3xl shadow-xl overflow-hidden border border-border relative">
          <div className="absolute top-0 inset-x-0 h-14 bg-gradient-to-r from-premium-500 to-premium-600 flex items-center justify-center">
            <p className="text-white font-bold text-sm uppercase tracking-widest flex items-center">
              <Star className="w-4 h-4 mr-2 fill-current" />
              30 days free
              <Star className="w-4 h-4 ml-2 fill-current" />
            </p>
          </div>

          <div className="pt-20 pb-10 px-8 sm:px-12 text-center">
            <h3 className="text-2xl font-bold text-foreground mb-2 font-display">
              After your trial
            </h3>
            <p className="text-muted-foreground mb-6">
              The full suite is $9/month: website, job-site photos, and
              credentials.
            </p>
            <div className="flex items-end justify-center mb-2">
              <span className="text-5xl font-extrabold text-foreground">
                $9
              </span>
              <span className="text-lg text-muted-foreground font-medium ml-2 mb-1">
                / month
              </span>
            </div>
            <p className="text-sm text-muted-foreground font-medium mb-8">
              for the full suite after trial
            </p>

            <p className="text-sm font-medium text-premium-600 mb-8 bg-premium-50 py-2 px-4 rounded-lg inline-block">
              30-day trial covers the whole suite.
            </p>

            <ul className="space-y-4 mb-10 text-left">
              {PRICING_FEATURES.map((feature, idx) => (
                <li
                  key={idx}
                  className="flex items-center text-foreground font-medium"
                >
                  <CheckCircle2 className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <ButtonLink
              to={routes.signup}
              variant="dark"
              size="lg"
              className="w-full py-4 rounded-xl text-lg flex items-center justify-center group"
            >
              Start 30-day trial
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </ButtonLink>
            <p className="text-muted-foreground text-sm mt-4 font-medium">
              Cancel anytime. No hidden fees.
            </p>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);
